import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

import type { GameType, LocationType } from "../types";
import Cards from "./cards";
import DesktopCards from "./desktop-cards";
import List from "./list";
import MapKit from "./map-kit";

interface GamesProps {
  games: GameType[];
}

const MAP_VIEW = "map";
const LIST_VIEW = "list";

export default function Games({ games }: GamesProps) {
  const [currentLocation, setCurrentLocation] = useState<LocationType | null>(
    null
  );
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(
    null
  );
  const [view, setView] = useState(MAP_VIEW);

  const handleLocation = useCallback((location: LocationType | null) => {
    setCurrentLocation((prev) => {
      if (location === null) {
        return prev === null ? prev : null;
      }
      return prev === null || prev.id !== location.id ? location : prev;
    });
  }, []);

  const handleView = (view: string) => () => setView(view);

  useEffect(() => {
    if (!games.length) {
      return;
    }

    const [firstGame] = games;
    handleLocation(firstGame.location);
  }, [games, handleLocation]);

  const locations = useMemo(() => {
    const tmpLocations = games.reduce(
      (accum, game) => {
        const { location } = game;
        if (!(location.id in accum)) {
          accum[location.id] = location;
        }
        return accum;
      },
      {} as Record<string, LocationType>
    );
    return Object.values(tmpLocations);
  }, [games]);

  return (
    <div
      className={cn(
        "flex w-full flex-col md:mt-[55px] md:flex-row",
        view === MAP_VIEW && "h-[calc(100vh-72px)] md:h-auto"
      )}
    >
      <DesktopCards
        currentLocation={currentLocation}
        games={games}
        onHover={setHoveredLocationId}
        onLocation={handleLocation}
      />
      <MapKit
        className={cn(
          "h-full md:sticky md:top-0 md:h-screen",
          view === LIST_VIEW && "h-20"
        )}
        currentLocation={currentLocation}
        hoveredLocationId={hoveredLocationId}
        locations={locations}
        onLocation={handleLocation}
      />
      {view === MAP_VIEW ? (
        <Cards
          currentLocation={currentLocation}
          games={games}
          onLocation={handleLocation}
          onView={handleView(LIST_VIEW)}
        />
      ) : (
        <List games={games} onView={handleView(MAP_VIEW)} />
      )}
    </div>
  );
}
